import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiUrl } from "../../config/apiBase";
import { useAuth } from "../../AuthContext";
import Sidebar from "../Sidebar";
import Feedback from "../Feedback";
import Footer from "../../Landing Page/Footer";
import { FaMicrophone, FaStop, FaPlay, FaTrash } from 'react-icons/fa';
import "./HRQuestionsContent.css";

function HRQuestionsContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshTokenIfNeeded } = useAuth();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  const [hrData, setHrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [interviewScoring, setInterviewScoring] = useState(null);

  // Audio recording state
  const [isRecording, setIsRecording] = useState({});
  const [audioUrls, setAudioUrls] = useState({});
  const [mediaRecorders, setMediaRecorders] = useState({});
  const [recordingTime, setRecordingTime] = useState({});
  const [isTranscribing, setIsTranscribing] = useState({});
  const [speechRecognitions, setSpeechRecognitions] = useState({});

  // Function to generate tailored tips based on question category
  const getQuestionTip = (question) => {
    const category = question.category;
    const questionText = question.question.toLowerCase();

    switch (category) {
      case 'professional_background':
        if (questionText.includes('experience') || questionText.includes('background')) {
          return '💡 Tip: Highlight your most relevant experience and quantify your achievements with specific metrics or timeframes';
        } else if (questionText.includes('role') || questionText.includes('responsibilities')) {
          return '💡 Tip: Describe your key responsibilities and how they relate to this position';
        }
        return '💡 Tip: Focus on your professional journey and key achievements that demonstrate your qualifications';

      case 'motivation_and_fit':
        if (questionText.includes('why') || questionText.includes('interested')) {
          return '💡 Tip: Connect your career goals with the company\'s mission and show genuine enthusiasm for the role';
        } else if (questionText.includes('company') || questionText.includes('culture')) {
          return '💡 Tip: Research the company and mention specific aspects that align with your values and career objectives';
        }
        return '💡 Tip: Show your passion for the role and explain why you\'re the right fit beyond just skills';

      case 'technical_skills':
        if (questionText.includes('experience') || questionText.includes('used')) {
          return '💡 Tip: Provide specific examples of how you\'ve applied these skills in real projects';
        } else if (questionText.includes('familiar') || questionText.includes('knowledge')) {
          return '💡 Tip: Be honest about your skill level and mention related experience or self-learning';
        }
        return '💡 Tip: Demonstrate both technical knowledge and practical application of relevant skills';

      case 'problem_solving':
        if (questionText.includes('challenge') || questionText.includes('difficult')) {
          return '💡 Tip: Use STAR method - describe the Situation, your Task, Actions you took, and the Results achieved';
        } else if (questionText.includes('approach') || questionText.includes('solve')) {
          return '💡 Tip: Explain your thought process and the steps you took to resolve the issue';
        }
        return '💡 Tip: Focus on your analytical thinking and how you approach complex problems systematically';

      case 'career_goals':
        if (questionText.includes('future') || questionText.includes('5 years')) {
          return '💡 Tip: Show ambition while staying realistic - connect your goals to growth opportunities in this role';
        } else if (questionText.includes('learn') || questionText.includes('develop')) {
          return '💡 Tip: Express eagerness to learn and grow while highlighting how you can contribute immediately';
        }
        return '💡 Tip: Demonstrate long-term thinking and how this role fits into your professional development';

      default:
        return '💡 Tip: Be specific, use examples, and show how your experience relates to this role';
    }
  };

  const fetchHRQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const token = await refreshTokenIfNeeded();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${apiUrl('/api/hr-screening/questions')}/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch HR questions: ${response.statusText}`);
      }

      const data = await response.json();
      setHrData(data);

      // Initialize answers array
      if (data.questions) {
        const initialAnswers = data.questions.map(q => ({
          questionId: q.id,
          answer: q.answer || ''
        }));
        setAnswers(initialAnswers);
      }

      // Check if already submitted
      if (data.answers) {
        setSubmitted(true);
        setAnswers(data.answers);
      }

      if (data.interview_performance_scoring) {
        setInterviewScoring(data.interview_performance_scoring);
      }

    } catch (err) {
      console.error("Error fetching HR questions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const handleAnswerChange = (questionIndex, answer) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = {
      questionId: hrData.questions[questionIndex].id,
      answer: answer
    };
    setAnswers(newAnswers);
  };

  const handleSubmitAnswers = async () => {
    try {
      setSubmitting(true);
      const token = await refreshTokenIfNeeded();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${apiUrl('/api/hr-screening/submit-answers')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          answers: answers
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to submit answers: ${response.statusText}`);
      }

      const data = await response.json();
      setInterviewScoring(data.interviewScoring);
      setSubmitted(true);

      // Auto-scroll to performance analysis after a short delay
      setTimeout(() => {
        const performanceElement = document.getElementById('performance-analysis');
        if (performanceElement) {
          performanceElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 500);

    } catch (err) {
      console.error("Error submitting answers:", err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Audio recording functions
  const startRecording = async (questionIndex) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);

        setAudioUrls(prev => ({ ...prev, [questionIndex]: url }));

        // Stop speech recognition when recording stops
        const recognition = speechRecognitions[questionIndex];
        if (recognition) {
          recognition.stop();
        }
      };

      // Start speech recognition for real-time transcription
      const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let finalTranscript = '';

      recognition.onresult = (event) => {
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Update the textarea with the combined transcript
        const newTranscript = finalTranscript + interimTranscript;
        if (newTranscript.trim()) {
          handleAnswerChange(questionIndex, newTranscript);
        }
      };

      recognition.onerror = (error) => {
        console.error('Speech recognition error:', error);
        // Don't show error alert during recording as it's common
      };

      recognition.onend = () => {
        setIsTranscribing(prev => ({ ...prev, [questionIndex]: false }));
      };

      // Start both recording and recognition
      mediaRecorder.start();
      recognition.start();

      setSpeechRecognitions(prev => ({ ...prev, [questionIndex]: recognition }));

      // Start recording timer
      setIsRecording(prev => ({ ...prev, [questionIndex]: true }));
      setRecordingTime(prev => ({ ...prev, [questionIndex]: 0 }));

      const timer = setInterval(() => {
        setRecordingTime(prev => ({
          ...prev,
          [questionIndex]: (prev[questionIndex] || 0) + 1
        }));
      }, 1000);

      // Store timer ID for cleanup
      setMediaRecorders(prev => ({
        ...prev,
        [questionIndex]: { recorder: mediaRecorder, timer, stream }
      }));

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = (questionIndex) => {
    const recorderData = mediaRecorders[questionIndex];
    if (recorderData) {
      recorderData.recorder.stop();
      recorderData.stream.getTracks().forEach(track => track.stop());
      clearInterval(recorderData.timer);
    }

    setIsRecording(prev => ({ ...prev, [questionIndex]: false }));
  };

  const playAudio = (questionIndex) => {
    const url = audioUrls[questionIndex];
    if (url) {
      const audio = new Audio(url);
      audio.play();
    }
  };

  const deleteAudio = (questionIndex) => {
    // Stop speech recognition if it's still running
    const recognition = speechRecognitions[questionIndex];
    if (recognition) {
      recognition.stop();
      setSpeechRecognitions(prev => {
        const newRecognitions = { ...prev };
        delete newRecognitions[questionIndex];
        return newRecognitions;
      });
    }

    // Clean up audio URL
    const url = audioUrls[questionIndex];
    if (url) {
      URL.revokeObjectURL(url);
    }

    setAudioUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[questionIndex];
      return newUrls;
    });

    // Clear the transcribed text
    handleAnswerChange(questionIndex, '');
  };

  useEffect(() => {
    if (sessionId) {
      fetchHRQuestions();
    } else {
      setError("No session ID provided");
      setLoading(false);
    }
  }, [sessionId, fetchHRQuestions]);

  if (loading) {
    return (
      <div className="hr-questions-container">
        <Sidebar activeItem="questions" />
        <div className="hr-main-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading HR screening questions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hr-questions-container">
        <Sidebar activeItem="questions" />
        <div className="hr-main-content">
          <div className="error-container">
            <h2>Error Loading Questions</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/dashboard')} className="back-btn">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hr-questions-container">
      <Sidebar activeItem="questions" />
      <div className="hr-main-content">
        <div className="hr-content-header">
          <h2>HR Screening Interview</h2>
          <p>Review your CV and prepare for these HR screening questions tailored to the job posting.</p>
        </div>

        <div className="hr-content-body">
          <div className="hr-layout">
            {/* Left side - CV Display */}
            <div className="cv-section">
              <h3 className="section-heading1">Areas to Focus on in Your CV/Resume</h3>
              <div className="cv-display">
                {hrData?.cv_file_url ? (
                  <iframe
                    src={hrData.cv_file_url}
                    className="cv-iframe"
                    title="Your CV"
                    frameBorder="0"
                  />
                ) : (
                  <div className="cv-placeholder">
                    <p>CV preview not available</p>
                    <p>Your uploaded CV will help generate personalized questions</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Questions */}
            <div className="questions-section">
              <h3 className="section-heading">
                HR Screening Questions
                {submitted && <span className="submitted-badge">✓ Completed</span>}
              </h3>

              <div className="questions-list">
                {hrData?.questions?.map((question, index) => (
                  <div key={question.id || index} className="question-item">
                    <div className="question-header">
                      <span className="question-number">Q{index + 1}</span>
                      <span className="question-category">{question.category}</span>
                    </div>
                    <div className="question-text">
                      {question.question}
                    </div>
                    {!submitted && (
                      <div className="answer-section">
                        <div className="answer-input-container">
                          <textarea
                            className="answer-input"
                            placeholder="Type your answer here... (Be specific and use examples where possible)"
                            value={answers[index]?.answer || ''}
                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                            rows={4}
                          />
                          <div className="audio-controls-container">
                            {!isRecording[index] && !audioUrls[index] && (
                              <button
                                type="button"
                                className="audio-btn record-btn"
                                onClick={() => startRecording(index)}
                                title="Record voice answer"
                              >
                                <FaMicrophone /> Record
                              </button>
                            )}

                            {isRecording[index] && (
                              <div className="recording-controls">
                                <button
                                  type="button"
                                  className="audio-btn stop-btn"
                                  onClick={() => stopRecording(index)}
                                  title="Stop recording"
                                >
                                  <FaStop /> Stop ({recordingTime[index] || 0}s)
                                </button>
                                <span className="recording-pulse">● Recording</span>
                              </div>
                            )}

                            {audioUrls[index] && !isRecording[index] && (
                              <div className="playback-controls">
                                <button
                                  type="button"
                                  className="audio-btn play-btn"
                                  onClick={() => playAudio(index)}
                                  title="Play recording"
                                >
                                  <FaPlay /> Play
                                </button>
                                <button
                                  type="button"
                                  className="audio-btn delete-btn"
                                  onClick={() => deleteAudio(index)}
                                  title="Delete recording"
                                >
                                  <FaTrash /> Delete
                                </button>
                                {isTranscribing[index] && (
                                  <span className="transcribing">Transcribing...</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="answer-tips">
                          <small>{getQuestionTip(question)}</small>
                        </div>
                      </div>
                    )}
                    {submitted && answers[index]?.answer && (
                      <div className="submitted-answer">
                        <strong>Your Answer:</strong>
                        <p>{answers[index].answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {!submitted && (
                <div className="submit-section">
                  <button
                    className="submit-answers-btn"
                    onClick={handleSubmitAnswers}
                    disabled={submitting || answers.some(a => !a.answer || a.answer.trim().length < 10)}
                  >
                    {submitting ? 'Analyzing Your Performance...' : 'Submit Answers for Performance Analysis'}
                  </button>
                  <p className="submit-note">
                    Complete all answers to receive detailed feedback on your interview performance
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Interview Performance Scoring Dashboard - Now appears below the questions */}
          {submitted && interviewScoring && (
            <div id="performance-analysis" className="scoring-dashboard">
              <div className="scoring-header">
                <h3 className="scoring-title">Interview Performance Analysis</h3>
                <div className="overall-score">
                  <div 
                    className="score-circle"
                    style={{
                      background: `conic-gradient(${
                        interviewScoring.overall_performance_score >= 80 ? '#28a745' : 
                        interviewScoring.overall_performance_score >= 30 ? '#ffc107' : 
                        '#dc3545'
                      } 0% ${interviewScoring.overall_performance_score}%, #e2e8f0 ${interviewScoring.overall_performance_score}% 100%)`
                    }}
                  >
                    <span className="score-number">{interviewScoring.overall_performance_score}</span>
                    <span className="score-label">Overall Performance</span>
                  </div>
                  <div className="recommendation">
                    <span className={`rec-badge ${interviewScoring.performance_level.toLowerCase().replace(' ', '-')}`}>
                      {interviewScoring.performance_level}
                    </span>
                  </div>
                </div>
              </div>
              <div className="score-breakdown">
                {Object.entries(interviewScoring.dimensions).map(([key, dimension]) => (
                  <div key={key} className="dimension-item">
                    <div className="dimension-header">
                      <span className="dimension-name">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span 
                        className="dimension-score"
                        style={{
                          background: dimension.score >= 80 ? '#f0fdf4' :
                                     dimension.score >= 60 ? '#fefce8' :
                                     dimension.score >= 40 ? '#fff7ed' :
                                     '#fef2f2',
                          color: dimension.score >= 80 ? '#166534' :
                                 dimension.score >= 60 ? '#92400e' :
                                 dimension.score >= 40 ? '#9a3412' :
                                 '#991b1b',
                          borderColor: dimension.score >= 80 ? '#bbf7d0' :
                                      dimension.score >= 60 ? '#fde68a' :
                                      dimension.score >= 40 ? '#fed7aa' :
                                      '#fecaca'
                        }}
                      >
                        {dimension.score}/100
                      </span>
                    </div>
                    <div className="score-bar">
                      <div
                        className="score-fill"
                        style={{
                          width: `${dimension.score}%`,
                          background: dimension.score >= 80 ? 'linear-gradient(90deg, #16a34a, #22c55e)' :
                                   dimension.score >= 60 ? 'linear-gradient(90deg, #ca8a04, #eab308)' :
                                   dimension.score >= 40 ? 'linear-gradient(90deg, #ea580c, #f97316)' :
                                   'linear-gradient(90deg, #dc2626, #ef4444)'
                        }}
                      ></div>
                    </div>
                    <div className="dimension-details">
                      <p className="reasoning">{dimension.reasoning}</p>
                      <div className="strengths-gaps">
                        <div className="strengths">
                          <strong>Strengths:</strong>
                          <ul>
                            {dimension.strengths.map((strength, idx) => (
                              <li key={idx}>{strength}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="gaps">
                          <strong>Areas to Improve:</strong>
                          <ul>
                            {dimension.improvement_areas.map((area, idx) => (
                              <li key={area}>{area}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>

        <Feedback />
      </div>
      <div className="dashboard-footer">
        <Footer />
      </div>
    </div>
  );
}

export default HRQuestionsContent;